import { useEffect, useCallback } from 'react';
import { useStripeStatus } from '@/hooks/useStripeStatus';
import { useItemCounts } from '@/hooks/useItemCounts';
import { globalProducts } from '@/types/product';
import { useGlobalEvents } from '@/hooks/useGlobalEvents';
import { useToast } from '@/hooks/use-toast';
import { useOnboardingState } from './useOnboardingState';
import { useOnboardingPersistence } from './useOnboardingPersistence';
import { useOnboardingEvents } from './useOnboardingEvents';
import { useBusinessProfileValidation } from './useBusinessProfileValidation';

export function useOnboardingChecklist() {
  const {
    state,
    setState,
    userId,
    setUserId,
    isInitialized,
    setIsInitialized,
    saveOnboardingState,
    updateState
  } = useOnboardingState();

  const { loadOnboardingState, updateSetupCompleted } = useOnboardingPersistence();
  const { isConnected: stripeConnected, isLoading: stripeLoading } = useStripeStatus();
  const { hasProducts, hasOffers, refreshCounts } = useItemCounts();
  const { dispatchCustomEvent } = useGlobalEvents();
  const { toast } = useToast();

  useEffect(() => {
    const init = async () => {
      const { user, savedState, isSetupCompleted } = await loadOnboardingState();
      
      if (!user) {
        setIsInitialized(true);
        return;
      }
      
      setUserId(user.id);
      
      if (savedState) {
        setState(prev => ({
          ...prev,
          ...savedState,
          setupCompleted: false, 
        }));
      } else {
        updateState({ accountVerified: true }); 
      }
      
      setIsInitialized(true);
      
      if (isSetupCompleted) {
        setState(prev => ({
          ...prev,
          accountVerified: true,
          stripeConnected: true,
          businessProfileCompleted: true,
          hasCreatedProduct: true,
          hasCreatedBundle: true,
          hasSharedLink: true,
          setupCompleted: true,
          isVisible: false,
        }));
      }
    };
    
    init();
  }, [])

  useBusinessProfileValidation({
    userId,
    isInitialized,
    businessProfileCompleted: state.businessProfileCompleted,
    saveOnboardingState
  });

  useOnboardingEvents({ saveOnboardingState, refreshCounts });

  useEffect(() => {
    if (!isInitialized) return;
    
    if (stripeConnected !== state.stripeConnected) {
      setState(prev => ({ ...prev, stripeConnected }));
      
      if (userId) {
        const updatedState = { ...state, stripeConnected };
        localStorage.setItem(`keepmore_onboarding_state_${userId}`, JSON.stringify(updatedState));
      }
    }
  }, [stripeConnected, state.stripeConnected, isInitialized, userId, state, setState]);

  // Update product creation status
  useEffect(() => {
    if (!isInitialized) return;
    
    const hasProduct = hasProducts || globalProducts.length > 0;
    
    if (hasProduct !== state.hasCreatedProduct) {
      saveOnboardingState({ hasCreatedProduct: hasProduct });
    }
  }, [hasProducts, state.hasCreatedProduct, isInitialized, saveOnboardingState]);

  // Update bundle creation status
  useEffect(() => {
    if (!isInitialized) return;
    
    if (hasOffers !== state.hasCreatedBundle) {
      saveOnboardingState({ hasCreatedBundle: hasOffers });
    }
  }, [hasOffers, state.hasCreatedBundle, isInitialized, saveOnboardingState]);

  useEffect(() => {
    if (!isInitialized || !userId) return;
    
    const isComplete = state.accountVerified && 
                      state.stripeConnected && 
                      state.businessProfileCompleted && 
                      state.hasCreatedProduct &&
                      (state.hasCreatedBundle && state.hasSharedLink);
    
    if (isComplete && !state.setupCompleted) {
      saveOnboardingState({ setupCompleted: true, isVisible: true });
    }
  }, [state, isInitialized, userId, saveOnboardingState]);

  const completeStep = useCallback((step: keyof Omit<typeof state, 'isVisible' | 'setupCompleted' | 'showCompletionConfirmation'>) => {
    saveOnboardingState({ [step]: true });
  }, [saveOnboardingState]);

  const finishOnboarding = useCallback(async () => {
    if (!userId) return;
    setState(prev => ({ ...prev, isVisible: false, setupCompleted: true }));
    await updateSetupCompleted(userId, true);
    
    toast({
      title: "Setup Complete!",
      description: "You're ready to start selling your products!",
      duration: 5000,
    });
  }, [userId, updateSetupCompleted, setState, toast]);

  const acknowledgeCompletion = useCallback(async () => {
    if (!userId) return;
    await updateSetupCompleted(userId, true);
    
    toast({
      title: "Setup Complete!",
      description: "You're ready to start selling your products!",
      duration: 5000,
    });
  }, [userId, updateSetupCompleted, setState, toast]);
  const markOnboardingAsDismissed = useCallback(() => {
    const isComplete = state.accountVerified && 
                      state.stripeConnected && 
                      state.businessProfileCompleted && 
                      state.hasCreatedProduct &&
                      (state.hasCreatedBundle && state.hasSharedLink);
    
    if (isComplete) {
      finishOnboarding();
    } else {
      setState(prev => ({ ...prev, isVisible: false }));
      sessionStorage.setItem(`onboarding_hidden_${userId}`, 'true');
    }
  }, [state, finishOnboarding, userId, setState]);

  const resetOnboarding = useCallback(async () => {
    if (!userId) return;
    
    const resetState = {
      isVisible: true,
      accountVerified: true,
      stripeConnected: false,
      businessProfileCompleted: false,
      hasCreatedProduct: false,
      hasCreatedBundle: false,
      hasSharedLink: false,
      setupCompleted: false,
      showCompletionConfirmation: false,
    };
    
    setState(resetState);
    localStorage.setItem(`keepmore_onboarding_state_${userId}`, JSON.stringify(resetState));
    sessionStorage.removeItem(`onboarding_hidden_${userId}`);

    await updateSetupCompleted(userId, false);
  }, [userId, updateSetupCompleted, setState]);

  const copyProductLink = useCallback(() => {
    if (!globalProducts.length) return;
    
    const product = globalProducts[0];
    const productUrl = `${window.location.origin}/pay/${product.id}`;
    
    navigator.clipboard.writeText(productUrl)
      .then(() => {
        dispatchCustomEvent('copy-link-success');
      })
      .catch(err => {
        console.error('Failed to copy product link:', err);
      });
  }, [globalProducts, dispatchCustomEvent]);

  return {
    ...state,
    hasOffers,
    hasCreatedBundle: state.hasCreatedBundle,
    isLoading: !isInitialized,

    progress: !isInitialized ? 0 : Math.round(
      (Number(state.accountVerified) + 
       Number(state.stripeConnected) + 
       Number(state.businessProfileCompleted) + 
       Number(state.hasCreatedProduct) +
       Number(state.hasCreatedBundle && state.hasSharedLink)) / 5 * 100
    ),

    completeStep,
    markOnboardingAsDismissed,
    finishOnboarding,
    acknowledgeCompletion,
    resetOnboarding,
    copyProductLink
  };
}
